import { Image } from "@/components/shared/image";
import type { QualificationType } from "@/generated/prisma/client";
import type { MediaAsset } from "@/lib/media";
import { cn } from "@/lib/utils";

// =============================================================================

export type TrainingItem = {
  id: string;
  /** "01", "02"… */
  number: string;
  type: QualificationType;
  title: string;
  cardTitle: string;
  missions: string[];
  tags: string[];
  /** "2024 – 2026", "2022 – Today", "Jan 2025" */
  period: string;
  isInProgress: boolean;
  credentialUrl: string | null;
  provider: { name: string; logo: MediaAsset | null } | null;
};

// =============================================================================

/**
 * White monochrome logo of the school or company, inverted in light theme.
 */
export function ProviderLogo({
  logo,
  className,
}: {
  logo: MediaAsset;
  className?: string;
}) {
  return (
    <span className={cn("relative block h-7 w-12 shrink-0", className)}>
      <Image
        src={logo.url}
        alt=""
        fill
        sizes="48px"
        className="object-contain object-left invert dark:invert-0"
        loadingClassName="bg-transparent"
      />
    </span>
  );
}

// =============================================================================

type TrainingDetailsProps = {
  item: TrainingItem;
  /** "Degree", "Experience", "Certification" */
  typeLabel: string;
  inProgressLabel: string;
  /** Floating card (fills its square) or accordion panel */
  variant: "card" | "panel";
  className?: string;
};

/**
 * Content of the hover card and of the accordion: logo and title, missions,
 * technologies, then the provider and the period.
 */
export function TrainingDetails({
  item,
  typeLabel,
  inProgressLabel,
  variant,
  className,
}: TrainingDetailsProps) {
  const { provider } = item;
  const isCard = variant === "card";

  return (
    <div
      className={cn("relative flex flex-col", isCard && "flex-1", className)}
    >
      {/* Short entries leave room: a faint giant logo fills the card */}
      {isCard && item.missions.length === 0 && provider?.logo && (
        <span
          aria-hidden
          className="pointer-events-none absolute right-0 bottom-12 h-[42%] w-[58%] bg-foreground opacity-[0.07]"
          style={{
            mask: `url("${provider.logo.url}") right bottom / contain no-repeat`,
            WebkitMask: `url("${provider.logo.url}") right bottom / contain no-repeat`,
          }}
        />
      )}

      <div className="relative flex items-center gap-4">
        {provider?.logo && <ProviderLogo logo={provider.logo} />}
        <div className="min-w-0">
          <p className="text-[1.25rem] leading-tight font-semibold">
            {item.cardTitle}
          </p>
          <p className="mt-1 text-xs tracking-[0.16em] text-muted-foreground uppercase">
            {typeLabel}
          </p>
        </div>
      </div>

      {item.missions.length > 0 && (
        <ul className="relative mt-6 space-y-4 text-[0.98rem] leading-snug">
          {item.missions.map((mission) => (
            <li key={mission} className="flex gap-3">
              <span
                aria-hidden
                className="mt-[0.55em] size-1.5 shrink-0 bg-foreground"
              />
              {mission}
            </li>
          ))}
        </ul>
      )}

      {item.tags.length > 0 && (
        <ul className="relative mt-6 flex flex-wrap gap-2">
          {item.tags.map((tag) => (
            <li
              key={tag}
              className="rounded-full border border-line px-3 py-1 text-xs text-muted-foreground"
            >
              {tag}
            </li>
          ))}
        </ul>
      )}

      <div
        className={cn(
          "relative flex flex-wrap items-end justify-between gap-x-4 gap-y-2 pt-8",
          isCard && "mt-auto"
        )}
      >
        {provider && (
          <p className="font-semibold text-foreground/80">
            {provider.name}
            <span className="text-brand">.</span>
          </p>
        )}
        <p className="flex items-center gap-2.5 text-muted-foreground tabular-nums">
          {item.isInProgress && (
            <span className="rounded-full border border-line px-2.5 py-0.5 text-xs text-foreground/80">
              {inProgressLabel}
            </span>
          )}
          {item.period}
        </p>
      </div>
    </div>
  );
}

// =============================================================================
